/**
 * **从 Area 的边/角拖出去分屏** —— 从 `layout/NpArea.vue` 向下拆出来的
 * 指针交互块（约 60 行）。
 *
 * ## ⚠️ 一条**踩过坑才写对**的规矩，别改回去
 *
 * **必须 `setPointerCapture`。** 不捕获的话，拖到画布上会被
 * d3-zoom / `touch-action: none` 抢走事件，表现为「**拖到一半断了**」。
 *
 * ⚠️ 但注意它和「变参行拖拽排序」那一处的取舍**相反**：
 * 那边**故意不**在 `pointerdown` 捕获（那会让双击失效）。
 * 判据是「这次手势要不要区分点击」——
 * 这里按下的就是把手本身（没有双击语义），所以立刻捕获；
 * 那边按下的可能是要双击改名的行，所以等位移超过阈值再捕获。
 *
 * ## 方向怎么定
 *
 * 取**位移大的那一轴**，和 Blender 一样：手往右拖就是在右边界分，
 * 往下拖就是在下边界分。**只看方向、不看位移多少** ——
 * 拖出去就算数，比例的夹取交给布局树。
 *
 * ## 归属
 *
 * **向下**：`NpArea.vue` → `composables/useSplitDrag.ts`，只有前者引用它。
 *
 * ⚠️ **函数体逐字不动**（这个仓库的搬运规矩）。
 */
import { onBeforeUnmount, ref } from 'vue'
import type { Ref } from 'vue'

export interface SplitDragCtx {
  /** 这块 Area 的根元素（量它的矩形来算比例） */
  rootEl: () => HTMLElement | null
  /** 拖完往哪分（宿主接成 `splitAt`） */
  splitAt: (dir: 'row' | 'col', ratio: number) => void
}

export function useSplitDrag(ctx: SplitDragCtx) {
  /** 正在拖的预览：方向 + 比例 + 手指位置（预览线按它画） */
  const splitDrag: Ref<{ dir: 'row' | 'col'; ratio: number; x: number; y: number } | null> =
    ref(null)
  let splitCleanup: (() => void) | null = null

  /**
   * 分割把手按下 —— 往哪拖就分到哪。
   *
   * ⚠️ 要 `setPointerCapture`：不捕获的话拖到画布上会被
   * d3-zoom / `touch-action: none` 抢走，表现为「拖到一半断了」。
   */
  function onSplitGripDown(ev: PointerEvent) {
    if (ev.button !== 0 && ev.pointerType === 'mouse') return
    const root = ctx.rootEl()
    if (!root) return
    ev.preventDefault()
    ev.stopPropagation()

    const el = ev.currentTarget as HTMLElement
    try {
      el.setPointerCapture(ev.pointerId)
    } catch {
      /* 不支持捕获时，window 上的监听仍能兜住 */
    }

    /*
     * ⚠️ 矩形的量取放在**真正要用的时候**（`onMove` / `onUp`），不在这里提前返回：
     *   · 点击（不拖动）那一路**根本不需要**矩形，而"rect 是 0"在无头环境里
     *     恒成立 —— 提前返回会让"点一下 = 左右分屏"在测试里永远走不到；
     *   · 真实浏览器里窗口也可能在拖动过程中被改大小，晚一点量更准。
     */
    const rectOf = () => root.getBoundingClientRect()

    const onMove = (e: PointerEvent) => {
      /* ⚠️ 还没超过点击阈值 → 不画预览线（点一下不该闪一条分割线） */
      if (Math.hypot(e.clientX - ev.clientX, e.clientY - ev.clientY) < 4) return
      /*
       * 方向取**位移大的那一轴** —— 和 Blender 一样：
       * 手往右拖就是在右边界分，往下拖就是在下边界分。
       */
      const dir: 'row' | 'col' =
        Math.abs(e.clientX - ev.clientX) >= Math.abs(e.clientY - ev.clientY) ? 'row' : 'col'
      const rect = rectOf()
      const ratio =
        dir === 'row'
          ? (rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0.5)
          : (rect.height > 0 ? (e.clientY - rect.top) / rect.height : 0.5)
      splitDrag.value = { dir, ratio, x: e.clientX, y: e.clientY }
    }
    /*
     * ⚠️ 卸载时的清理**不能**走 `onUp`：那条路会真的分屏
     * （组件都拆了还发 `splitAt` 是错的）。所以给个不带事件的取消口。
     */
    const cancel = () => {
      splitDrag.value = null
      splitCleanup = null
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }

    const onUp = (e: PointerEvent) => {
      const pending = splitDrag.value
      splitDrag.value = null
      splitCleanup = null
      try {
        el.releasePointerCapture(ev.pointerId)
      } catch {
        /* 已经释放过 */
      }
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      /*
       * ★ **没拖动 = 点击**：默认左右分屏（真机反馈："只保留那个像放大一样的
       * 按钮，让它支持两种语义，一种是点击之后默认左右分屏，一种就是按住
       * 上下移动就是上下分屏，左右移动就是左右分屏"）。
       *
       * ⚠️ 阈值按**位移**算（4px）而不是时间：手抖一两像素不该被当成拖动，
       * 而"按住停一会儿再松手"仍然是点击 —— 用户的心智是"我拖了吗"。
       */
      const moved = Math.hypot(e.clientX - ev.clientX, e.clientY - ev.clientY)
      if (!pending) {
        if (moved < 4) ctx.splitAt('row', 0.5)
        return
      }
      /* 只看方向、不看位移多少 —— 拖出去就算数，夹取交给布局树 */
      ctx.splitAt(pending.dir, pending.ratio)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    splitCleanup = cancel
  }

  onBeforeUnmount(() => splitCleanup?.())

  return { splitDrag, onSplitGripDown }
}
